// Keep the in-flight bytes across browsers until the actual image loader consumes them.
// An HTTP-cache prefetch alone can start a second request while the first is
// still running, or force another validation round trip on Safari.
export function createDeviceImageDownloads(fetchImage = url => fetch(url, {priority: 'low', cache: 'force-cache'})) {
  const pending = new Map();
  return {
    prefetch(url) {
      if (pending.has(url)) return;
      const request = Promise.resolve().then(() => fetchImage(url)).then(response => {
        if (!response.ok) throw new Error(`Device image request failed (${response.status})`);
        return response.blob();
      });
      pending.set(url, request);
      // A failed speculative request must not poison the actual loader.
      request.catch(() => { if (pending.get(url) === request) pending.delete(url); });
    },
    take(url) {
      const request = pending.get(url);
      pending.delete(url); // Release compressed bytes once the decoder finishes.
      return request;
    },
  };
}

export const deviceImageDownloads = createDeviceImageDownloads();

export async function decodeDeviceBlob(blob, loader) {
  if (loader.isImageBitmapLoader)
    return createImageBitmap(blob, {...loader.options, colorSpaceConversion: 'none'});
  // Preserve Three's older-Safari HTMLImageElement compatibility path.
  const url = URL.createObjectURL(blob);
  try {
    return await new Promise((resolve, reject) => loader.load(url, texture => {
      resolve(texture.image);
      texture.dispose();
    }, undefined, reject));
  } finally { URL.revokeObjectURL(url); }
}
